/**
 * Post-login home routing helper.
 *
 * A "case-only" user is an org-less Clinician Connect participant: they resolve
 * `case:view` but NOT `order:view` (the isolation invariant from Path B — they
 * never resolve order/admin/org permissions). Their home is the case list
 * (`/cases`), not the order workspace (`/orders`), which would dead-end them.
 * Everyone with `order:view` (org members) keeps `/orders`.
 *
 * A user who resolves NEITHER `order:view` nor `case:view` has no usable access
 * yet — e.g. an authenticated identity whose invitation is still pending/not
 * accepted, or a user not provisioned in any org. Routing them to `/orders`
 * dead-ends on a "no permission" empty table; instead send them to a friendly
 * `/no-access` landing that explains they may need to accept an invitation.
 *
 * Pure and dependency-free so it can be unit-tested and shared by the login
 * callback and the first-time welcome screen.
 */
export const CASE_ONLY_HOME = '/cases'
export const DEFAULT_HOME = '/orders'
export const NO_ACCESS_HOME = '/no-access'

export function homePathForPermissions(permissions: readonly string[]): string {
  const hasCaseView = permissions.includes('case:view')
  const hasOrderView = permissions.includes('order:view')
  if (hasOrderView) {
    return DEFAULT_HOME
  }
  if (hasCaseView) {
    return CASE_ONLY_HOME
  }
  // No usable access resolved (e.g. an authenticated user whose invite is still
  // pending / not yet accepted, or an unprovisioned identity).
  return NO_ACCESS_HOME
}
